/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  debounce,
  FormControl,
  IconButton,
  Link,
  MenuItem,
  Stack,
  Tab,
  Tooltip,
  Typography,
  Unstable_Grid2 as Grid,
  CircularProgress,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import Breadcrumbs from "components/common/Breadcrumbs";
import { useCallback, useEffect, useMemo, useState } from "react";
import theme from "lib/theme";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { AppSelect, InputBase } from "components/common/Input";
import { useSearchParams } from "react-router-dom";
import useSearchParamsOperations from "hooks/useSearchParamsOperations";
import usePickups from "hooks/pickups/usePickups";
import { formatDate } from "utils/helpers";
import useLocations from "hooks/locations/useLocations";
import useAssets from "hooks/assets/useAssets";
import useAnalytics from "hooks/analytics/useAnalytics";
import { SecurityLocks } from "./SecurityLocks";
import { AssetsGrid } from "./AssetsDataGrid";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { MenuProps } from "constants/filterMenuProps";
import useFeatureFlag from "hooks/useFeatureFlag";
import { PickupQuickFilterAutocomplete } from "./PickupQuickFilterAutocomplete";
import { NotFoundAssetModal } from "./NotFoundAssetModal";
import AssetsFiltersToolbar from "./AssetsFiltersToolbar";

const stateFilters = [
  { label: "Available", value: "Available" },
  { label: "Enrolled", value: "Enrolled" },
  { label: "Checked Out", value: "Checked Out" },
  { label: "Pending Pickup", value: "Pending Pickup" },
  { label: "Verifying Release", value: "Verifying Release" },
  { label: "Retired", value: "Retired" },
  { label: "Repurposed", value: "Repurposed" },
  { label: "Recycled", value: "Recycled" },
];

const SEARCH_HELP_TEXT =
  "The search matches on serial number, model, vendor, manufacturer, device type, accessories and asset tag.";

function SearchFieldWithHelp({
  onChange,
}: {
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}) {
  return (
    <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0, width: "100%" }}>
      <InputBase
        placeholder="Search"
        sx={{
          flex: 1,
          minWidth: 0,
          "&:hover, &:focus": { zIndex: 2 },
        }}
        onChange={onChange}
      />
      <Tooltip
        title={
          <Typography variant="body2" component="span" sx={{ display: "block", maxWidth: 320 }}>
            {SEARCH_HELP_TEXT}
          </Typography>
        }
        arrow
        placement="top-start"
        enterTouchDelay={0}
      >
        <IconButton
          aria-label="What the search field matches"
          size="small"
          edge="end"
          sx={{ color: "text.secondary", flexShrink: 0 }}
        >
          <InfoOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

function Assets() {
  const {
    assets,
    setAssets,
    setIsLoading,
    isLoading,
    isError,
    meta,
    refetch,
    setSelectedAssetIds,
    selectedAssets,
    selectedAssetIds,
    bulkUnenroll,
    bulkPendingPickup,
    statuses,
    setSelectAllFlag,
    selectAllFlag,
    getAllSerialsNumbers,
  } = useAssets();
  const [searchParams] = useSearchParams();
  const { createAnalyticsEvent } = useAnalytics();
  const [eventTracked, setEventTracked] = useState(false);
  const { replace, deleteParam, deleteFieldInArrayUsingPrefix, replaceInArrayFieldUsingPrefix } =
    useSearchParamsOperations();
  const [pickupIds, setPickupIds] = useState(
    searchParams
      .getAll("filter[]")
      .find((p) => p.includes("pickup_id,is_any_of"))
      ?.split("pickup_id,is_any_of,")[1]
      ?.split(",") || []
  );
  const [locationId, setLocationId] = useState(
    searchParams
      .getAll("filter[]")
      .find((p) => p.includes("location_id,is"))
      ?.split("location_id,is,")[1] || ""
  );
  const [status, setStatus] = useState(
    searchParams
      .getAll("filter[]")
      .find((p) => p.includes("status,is"))
      ?.split("status,is,")[1] || ""
  );
  const [securityLocksOpen, setSecurityLocksOpen] = useState(false);
  const [notFoundOpen, setNotFoundOpen] = useState(false);

  const { enabled: improveAssetDataEnabled } = useFeatureFlag("improve_asset_data");
  const { enabled: enrollmentV2Enabled } = useFeatureFlag("enrollment_v2");
  const { enabled: assetsNewFiltersEnabled, loading: assetsNewFiltersLoading } =
    useFeatureFlag("assets_new_filters_enabled");
  const [pickupCatalogRequested, setPickupCatalogRequested] = useState(false);
  const [locationCatalogRequested, setLocationCatalogRequested] = useState(false);
  const requestCatalog = useCallback((catalog: "pickup" | "location") => {
    if (catalog === "pickup") setPickupCatalogRequested(true);
    if (catalog === "location") setLocationCatalogRequested(true);
  }, []);
  const pickupsEnabled = assetsNewFiltersEnabled === false || pickupCatalogRequested;
  const locationsEnabled = assetsNewFiltersEnabled === false || locationCatalogRequested;
  const {
    pickups,
    errorMessage,
    isLoading: filterIsLoading,
    hasNextPage,
    incrementCurrentPage,
  } = usePickups(true, {
    enabled: pickupsEnabled,
  });
  const { locations, isLoading: locationsLoading } = useLocations(undefined, locationsEnabled);

  const [sentryRef] = useInfiniteScroll({
    loading: filterIsLoading,
    hasNextPage,
    onLoadMore: incrementCurrentPage,
    disabled: !!errorMessage,
    rootMargin: "0px 0px 400px 0px",
  });

  const availableStateFilters = useMemo(() => stateFilters.filter(({ value }) => statuses.includes(value)), [statuses]);

  const handleSearchChange = useCallback(
    debounce((replaceParamsFn: (f: string, v: string) => void, search: string) => {
      replaceParamsFn("q", search);
    }, 200),
    []
  );

  const cleanFilters = useCallback(() => {
    setPickupIds([]);
    setLocationId("");
    setStatus("");
    deleteParam("filter[]");
    deleteParam("filter_link_operator");
  }, [deleteParam]);

  useEffect(() => {
    if (assetsNewFiltersEnabled) return;
    if (pickupIds && pickupIds.length > 0) {
      replaceInArrayFieldUsingPrefix("filter[]", "pickup_id,is_any_of", `pickup_id,is_any_of,${pickupIds.join(",")}`);
    } else {
      deleteFieldInArrayUsingPrefix("filter[]", "pickup_id,is_any_of");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupIds]);

  useEffect(() => {
    if (assetsNewFiltersEnabled) return;
    if (locationId && locationId.length > 0) {
      replaceInArrayFieldUsingPrefix("filter[]", "location_id,is", `location_id,is,${locationId}`);
    } else {
      deleteFieldInArrayUsingPrefix("filter[]", "location_id,is");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  useEffect(() => {
    if (assetsNewFiltersEnabled) return;
    if (status && status.length > 0) {
      replaceInArrayFieldUsingPrefix("filter[]", "status,is", `status,is,${status}`);
    } else {
      deleteFieldInArrayUsingPrefix("filter[]", "status,is");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    if (assetsNewFiltersEnabled !== false) return;
    deleteFieldInArrayUsingPrefix("filter[]", "needs_attention,is,");
  }, [assetsNewFiltersEnabled]);

  useEffect(() => {
    if (eventTracked) return;
    setEventTracked(true);
    createAnalyticsEvent("assets_page_visit");
  }, [eventTracked]);

  const gridTabChanged = () => {
    setAssets([]);
  };

  return (
    <div>
      <Box>
        <Breadcrumbs data={[{ label: "Dashboard", to: "/" }, { label: "Assets" }]}></Breadcrumbs>
      </Box>

      <Typography variant="h4" mt={3} fontWeight="bold">
        Assets
      </Typography>

      <Typography variant="body1" mt={1} mb={3}>
        Manage your assets all in one place.{" "}
        {improveAssetDataEnabled && (
          <Link href="https://www.revivn.com/asset-list-faq" target="_blank">
            Learn more about our process and reporting.
          </Link>
        )}
      </Typography>

      {improveAssetDataEnabled && (
        <Grid container spacing={4}>
          <Grid xs={12}>
            {assetsNewFiltersLoading ? (
              <CircularProgress size={20} />
            ) : assetsNewFiltersEnabled ? (
              <AssetsFiltersToolbar
                pickups={pickups}
                locations={locations}
                pickupListItemRef={sentryRef}
                onCatalogNeeded={requestCatalog}
                pickupsLoading={filterIsLoading}
                locationsLoading={locationsLoading}
              />
            ) : (
              <Stack
                gap={2}
                alignItems="center"
                display="grid"
                sx={{ gridTemplateColumns: { lg: "auto 1fr minmax(100px, .2fr) minmax(120px, .35fr)", xs: "1fr" } }}
              >
                <Typography>Quick Filters:</Typography>
                <Stack
                  gap={2}
                  justifyContent="stretch"
                  alignItems="center"
                  display="grid"
                  sx={{
                    gridTemplateColumns: {
                      md: enrollmentV2Enabled ? "repeat(3, 1fr)" : "repeat(4, 1fr)",
                      xs: "1fr 1fr",
                    },
                  }}
                >
                  {enrollmentV2Enabled ? (
                    <PickupQuickFilterAutocomplete
                      size="small"
                      pickupIds={pickupIds}
                      setPickupIds={setPickupIds}
                      pickupCatalog={pickups}
                    />
                  ) : (
                    <FormControl variant="standard" size="small">
                      <AppSelect
                        multiple
                        value={pickupIds}
                        MenuProps={MenuProps}
                        onChange={(e) => setPickupIds(e.target.value as string[])}
                        renderValue={(values) =>
                          values.length === 0
                            ? "Pickup ID"
                            : values.length === 1
                            ? pickups.find(({ id }) => id.toString() === values[0].toString())?.orderNumber
                            : `${values.length} pickups selected`
                        }
                        displayEmpty
                      >
                        {pickups.map((pickup) => (
                          <MenuItem key={pickup.id} value={pickup.id} ref={sentryRef}>
                            {pickup.orderNumber}
                          </MenuItem>
                        ))}
                      </AppSelect>
                    </FormControl>
                  )}
                  {!enrollmentV2Enabled && (
                    <FormControl variant="standard" size="small">
                      <AppSelect
                        multiple
                        value={pickupIds}
                        MenuProps={MenuProps}
                        onChange={(e) => setPickupIds(e.target.value as string[])}
                        renderValue={(values) =>
                          values.length === 0
                            ? "Pickup Date"
                            : values.length === 1
                            ? formatDate(
                                pickups.find(({ id }) => id.toString() === values[0].toString())?.scheduledAt || ""
                              )
                            : `${values.length} pickups selected`
                        }
                        displayEmpty
                      >
                        {pickups
                          .filter(({ scheduledAt }) => scheduledAt)
                          .map((pickup) => (
                            <MenuItem key={pickup.id} value={pickup.id} ref={sentryRef}>
                              {formatDate(pickup.scheduledAt)} - {pickup.orderNumber}
                            </MenuItem>
                          ))}
                      </AppSelect>
                    </FormControl>
                  )}
                  <FormControl variant="standard">
                    <AppSelect
                      value={locationId}
                      placeholder="Location"
                      onChange={(e) => setLocationId(e.target.value.toString())}
                      displayEmpty
                    >
                      <MenuItem value="">Location</MenuItem>
                      {locations.map((location) => (
                        <MenuItem key={location.id} value={location.id}>
                          {location.name}
                        </MenuItem>
                      ))}
                    </AppSelect>
                  </FormControl>
                  <FormControl variant="standard">
                    <AppSelect
                      value={status}
                      placeholder="Asset Status"
                      onChange={(e) => setStatus(e.target.value.toString())}
                      displayEmpty
                    >
                      <MenuItem value="">Status</MenuItem>
                      {availableStateFilters.map(({ label, value }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </AppSelect>
                  </FormControl>
                </Stack>
                <div>
                  <Button variant={"outlined"} onClick={cleanFilters} sx={{ width: { xs: "100%", lg: "auto" } }}>
                    Clear All
                  </Button>
                </div>
                <SearchFieldWithHelp onChange={(e) => handleSearchChange(replace, e.target.value)} />
              </Stack>
            )}
          </Grid>
          <Grid xs={12}>
            {selectedAssets && selectedAssets[0] && (
              <>
                <SecurityLocks
                  open={securityLocksOpen}
                  setOpen={setSecurityLocksOpen}
                  bulkUnenroll={bulkUnenroll}
                  selectedAssetIds={selectedAssetIds}
                  selectAllFlag={selectAllFlag}
                />
              </>
            )}

            <AssetsGrid
              fieldsToHide={["assignedTo"]}
              replace={replace}
              assets={assets}
              isLoading={isLoading}
              isError={isError}
              meta={meta}
              refetch={refetch}
              setSelectedAssetIds={setSelectedAssetIds}
              selectedAssetIds={selectedAssetIds}
              setSelectAllFlag={setSelectAllFlag}
              selectAllFlag={selectAllFlag}
              setSecurityLocksOpen={setSecurityLocksOpen}
              getAllSerialsNumbers={getAllSerialsNumbers}
              setNotFoundOpen={setNotFoundOpen}
            />
          </Grid>
        </Grid>
      )}

      {!improveAssetDataEnabled && (
        <Box
          sx={{ mt: 3, padding: 4, background: "white", borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}
        >
          <Typography variant="h6">Search your assets</Typography>
          <Typography variant="body1">Find the asset you need by searching for keywords or using filters. </Typography>

          <Box sx={{ mt: 3 }}>
            {assetsNewFiltersLoading ? (
              <CircularProgress size={20} />
            ) : assetsNewFiltersEnabled ? (
              <AssetsFiltersToolbar
                pickups={pickups}
                locations={locations}
                pickupListItemRef={sentryRef}
                onCatalogNeeded={requestCatalog}
                pickupsLoading={filterIsLoading}
                locationsLoading={locationsLoading}
              />
            ) : (
              <>
                <Box sx={{ maxWidth: "630px", width: "100%" }}>
                  <SearchFieldWithHelp onChange={(e) => handleSearchChange(replace, e.target.value)} />
                </Box>
                <Box
                  display="grid"
                  gridTemplateColumns="auto 1fr 1fr 1fr 1fr 1fr auto"
                  columnGap="1em"
                  marginY={2}
                  alignItems="center"
                >
                  <Typography width="100%">Quick Filters:</Typography>
                  {enrollmentV2Enabled ? (
                    <PickupQuickFilterAutocomplete
                      size="small"
                      pickupIds={pickupIds}
                      setPickupIds={setPickupIds}
                      pickupCatalog={pickups}
                    />
                  ) : (
                    <FormControl variant="standard" size="small" sx={{ width: "100%" }}>
                      <AppSelect
                        multiple
                        value={pickupIds}
                        MenuProps={MenuProps}
                        onChange={(e) => setPickupIds(e.target.value as string[])}
                        renderValue={(values) =>
                          values.length === 0
                            ? "Pickup Date"
                            : values.length === 1
                            ? formatDate(
                                pickups.find(({ id }) => id.toString() === values[0].toString())?.scheduledAt || ""
                              )
                            : `${values.length} pickups selected`
                        }
                        displayEmpty
                      >
                        {pickups
                          .filter(({ scheduledAt }) => scheduledAt)
                          .map((pickup) => (
                            <MenuItem key={pickup.id} value={pickup.id} ref={sentryRef}>
                              {formatDate(pickup.scheduledAt)} - {pickup.orderNumber}
                            </MenuItem>
                          ))}
                      </AppSelect>
                    </FormControl>
                  )}
                  <FormControl variant="standard" sx={{ width: "100%" }}>
                    <AppSelect
                      placeholder="Location"
                      value={locationId}
                      onChange={(e) => setLocationId(e.target.value.toString())}
                      displayEmpty
                    >
                      <MenuItem value="">Location</MenuItem>
                      {locations.map((location) => (
                        <MenuItem key={location.id} value={location.id}>
                          {location.name}
                        </MenuItem>
                      ))}
                    </AppSelect>
                  </FormControl>
                  <FormControl variant="standard" sx={{ width: "100%" }}>
                    <AppSelect
                      placeholder="Asset Status"
                      value={status}
                      onChange={(e) => setStatus(e.target.value.toString())}
                      displayEmpty
                    >
                      <MenuItem value="">Status</MenuItem>
                      {availableStateFilters.map(({ label, value }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </AppSelect>
                  </FormControl>
                  <Button variant={"outlined"} onClick={cleanFilters}>
                    Clear All
                  </Button>
                </Box>
              </>
            )}
          </Box>

          <Box sx={{ mt: 3, ".MuiTabPanel-root": { padding: 0 } }}>
            <TabContext value="retired">
              <Box>
                <TabList
                  aria-label="Assets tabs"
                  sx={{
                    textTransform: "capitalize",
                    ".MuiTabs-flexContainer": {
                      display: "inline-flex",
                      border: "1px solid",
                      borderColor: theme.palette.divider,
                    },
                    ".MuiButtonBase-root": { minWidth: "230px" },
                  }}
                >
                  <Tab
                    label="retired"
                    value="retired"
                    onClick={() => gridTabChanged()}
                    disabled={true}
                    sx={{
                      "&.Mui-disabled": {
                        color: "#0089BD",
                      },
                    }}
                  />
                </TabList>
              </Box>

              {selectedAssets && selectedAssets[0] && (
                <>
                  <SecurityLocks
                    open={securityLocksOpen}
                    setOpen={setSecurityLocksOpen}
                    bulkUnenroll={bulkUnenroll}
                    selectedAssetIds={selectedAssetIds}
                    selectAllFlag={selectAllFlag}
                  />
                </>
              )}

              <TabPanel value="retired">
                <AssetsGrid
                  fieldsToHide={["assignedTo"]}
                  replace={replace}
                  assets={assets}
                  isLoading={isLoading}
                  isError={isError}
                  meta={meta}
                  refetch={refetch}
                  setSelectedAssetIds={setSelectedAssetIds}
                  selectedAssetIds={selectedAssetIds}
                  setSelectAllFlag={setSelectAllFlag}
                  selectAllFlag={selectAllFlag}
                  setSecurityLocksOpen={setSecurityLocksOpen}
                  getAllSerialsNumbers={getAllSerialsNumbers}
                  setNotFoundOpen={setNotFoundOpen}
                />
              </TabPanel>
            </TabContext>
          </Box>
        </Box>
      )}

      <NotFoundAssetModal
        open={notFoundOpen}
        setOpen={setNotFoundOpen}
        selectedAssetIds={selectedAssetIds}
        selectAllFlag={selectAllFlag}
        refetch={refetch}
      />
    </div>
  );
}

export default Assets;
